import React, { useCallback, useContext, useState } from 'react'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import RadioButtonInput from 'components/Luxkit/RadioButton/RadioButtonInput'
import Heading from 'components/Luxkit/Typography/Heading'
import { ModalElementContext } from 'contexts/ModalProvider'
import { capitalise } from 'lib/stringUtils'
import {
  MyTripsFilterOptions,
  MyTripsSortOptions,
  MY_TRIPS_FILTER_OPTIONS,
  MY_TRIPS_SORT_OPTIONS,
} from 'tripPlanner/pages/myTrips/MyTripsListGrid'

interface Props {
  onFilterApply: (opt: MyTripsFilterOptions | null) => void
  onSortApply: (opt: MyTripsSortOptions) => void
  selectedSortOption: MyTripsSortOptions
  selectedFilter: MyTripsFilterOptions | null
  isFilterVisible: boolean
}

export default function MyTripsSortAndFilter({
  onFilterApply,
  onSortApply,
  selectedSortOption,
  selectedFilter,
  isFilterVisible,
}: Props) {
  const modalElement = useContext(ModalElementContext)
  const [sortOption, setSortOption] =
    useState<MyTripsSortOptions>(selectedSortOption)
  const [filter, setFilter] = useState<MyTripsFilterOptions | null>(
    selectedFilter,
  )

  const onApply = useCallback(() => {
    onFilterApply(filter)
    onSortApply(sortOption)
    modalElement?.resolve()
  }, [modalElement, filter, onFilterApply, onSortApply, sortOption])

  const onClickFilter = useCallback(
    (opt: MyTripsFilterOptions | null) => {
      if (opt === filter) {
        setFilter(null)
      } else {
        setFilter(opt)
      }
    },
    [filter, setFilter],
  )

  return (
    <>
      <ModalHeader title={isFilterVisible ? 'Sort & Filter' : 'Sort by'} />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={24}>
            {isFilterVisible && <Heading variant="heading5">Sort by</Heading>}
            <VerticalSpacer gap={12}>
              {MY_TRIPS_SORT_OPTIONS.map((opt) => (
                <div key={opt}>
                  <RadioButtonInput
                    onClick={() => setSortOption(opt)}
                    checked={opt === sortOption}
                  >
                    {capitalise(opt)}
                  </RadioButtonInput>
                </div>
              ))}
            </VerticalSpacer>
            {isFilterVisible && (
              <>
                <Heading variant="heading5">Filter by</Heading>
                <VerticalSpacer gap={12}>
                  {MY_TRIPS_FILTER_OPTIONS.map((opt) => (
                    <div key={opt}>
                      <RadioButtonInput
                        onClick={() => onClickFilter(opt)}
                        checked={filter === opt}
                      >
                        {capitalise(opt)}
                      </RadioButtonInput>
                    </div>
                  ))}
                </VerticalSpacer>
              </>
            )}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Apply',
          onClick: onApply,
        }}
      />
    </>
  )
}
